import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, ArrowRight, Loader2, ExternalLink, Sparkles, Beaker, Image as ImageIcon, FileText, Video, Link as LinkIcon, Figma, BookOpen, Music, Lock } from "lucide-react";
import DemoLayout from "@/components/DemoLayout";
import Collapsible from "@/components/Collapsible";
import { normalizePiece } from "@/lib/portfolio";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PIECE_ICONS = { image: ImageIcon, video: Video, pdf: FileText, url: LinkIcon, figma: Figma, case_study: BookOpen, audio: Music, on_request: Lock };

const ANALYZE_STEPS = [
  "Loading [PERSONA] profile",
  "Loading job description",
  "Pulling raw resume from profile",
  "Cross-referencing portfolio pieces with posting keywords",
  "Selecting most relevant pieces (3 to 6 of 10)",
  "Rewriting case study descriptions in posting vocabulary",
  "Drafting tailored resume",
  "Drafting cover letter in [PERSONA]'s voice",
  "Building per-application portfolio page",
  "Materials ready",
];

export default function DemoApplicationReview() {
  const { jobId } = useParams();
  const [params] = useSearchParams();
  const personaId = params.get("persona");
  const navigate = useNavigate();

  const [persona, setPersona] = useState(null);
  const [data, setData] = useState(null);
  const [phase, setPhase] = useState("analyzing"); // analyzing | ready
  const [stepIdx, setStepIdx] = useState(0);

  useEffect(() => {
    if (!personaId) { navigate("/demo", { replace: true }); return; }
    Promise.all([
      axios.get(`${API}/demo/profile/${personaId}`),
      axios.get(`${API}/demo/application/${personaId}/${jobId}`),
    ]).then(([p, app]) => {
      setPersona(p.data.profile);
      setData(app.data);
    }).catch(() => navigate(`/demo/dashboard?persona=${personaId}`, { replace: true }));
  }, [personaId, jobId, navigate]);

  // Run the analyzing animation. Properly cleanable so StrictMode double-mount
  // doesn't strand timers.
  useEffect(() => {
    if (phase !== "analyzing" || !persona) return undefined;
    let cancelled = false;
    let pending;
    let i = 0;
    const tick = () => {
      if (cancelled) return;
      i += 1;
      setStepIdx(i);
      if (i < ANALYZE_STEPS.length - 1) {
        pending = setTimeout(tick, 240);
      } else {
        pending = setTimeout(() => { if (!cancelled) setPhase("ready"); }, 400);
      }
    };
    pending = setTimeout(tick, 100);
    return () => { cancelled = true; clearTimeout(pending); };
  }, [phase, persona]);

  // For hover-reveal of the original (untailored) text on rewritten pieces.
  const originalById = useMemo(() => {
    const out = {};
    for (const p of (data?.all_pieces || [])) out[p.id] = p;
    return out;
  }, [data]);

  if (!data || !persona) {
    return <DemoLayout><div className="p-12 text-center text-muted-foreground text-xs uppercase tracking-[0.3em]">LOADING<span className="blink-caret"/></div></DemoLayout>;
  }

  const { job, user_job, selected_pieces } = data;
  const mats = user_job.materials;
  const summary = job.summary;

  return (
    <DemoLayout persona={persona}>
      <div className="max-w-[1600px] mx-auto px-5 md:px-12 py-6 sm:py-8 relative">
        <button onClick={() => navigate(`/demo/dashboard?persona=${personaId}`)} className="btn-ghost inline-flex items-center gap-2 mb-6" data-testid="demo-back-to-feed">
          <ArrowLeft className="w-3 h-3" /> Back to feed
        </button>

        {/* Header */}
        <div className="panel p-5 sm:p-8 mb-px relative">
          <div className="flex items-start justify-between flex-wrap gap-3 sm:gap-4">
            <div className="min-w-0 flex-1">
              <div className="terminal-label text-primary mb-2">[ APPLICATION / {job.id.replace("demo-job-", "").toUpperCase()} ]</div>
              <h1 className="font-display text-xl sm:text-2xl md:text-4xl uppercase font-bold tracking-tight break-words leading-tight">{job.title}</h1>
              <div className="text-xs sm:text-sm text-muted-foreground mt-2 break-words">{job.company} · {job.location}</div>
              {job.salary && <div className="text-xs text-accent-green font-mono mt-1 break-words">{job.salary}</div>}
            </div>
            <div className="flex items-center gap-3 sm:gap-3 flex-wrap w-full sm:w-auto">
              {job.url && (
                <a href={job.url} target="_blank" rel="noopener noreferrer" className="btn-secondary inline-flex items-center gap-2 text-[10px]">
                  <ExternalLink className="w-3 h-3" /> Original posting
                </a>
              )}
              <div className="flex items-center gap-5 sm:gap-4 ml-auto sm:ml-0">
                <Score label="Match" value={user_job.match_score} color="#00FF41" />
                <Score label="Enjoy" value={user_job.preference_score} color="#FFB000" />
              </div>
            </div>
          </div>
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-2">
            <Collapsible
              label="Why it matches"
              accent="green"
              summary={user_job.match_reasoning}
              testId="demo-why-it-matches"
            >
              <p className="text-sm text-foreground/85 leading-relaxed">{user_job.match_reasoning}</p>
            </Collapsible>
            <Collapsible
              label="Why you'd enjoy it"
              accent="amber"
              summary={user_job.preference_reasoning}
              testId="demo-why-youd-enjoy"
            >
              <p className="text-sm text-foreground/85 leading-relaxed">{user_job.preference_reasoning}</p>
            </Collapsible>
          </div>
        </div>

        {/* Role overview - headline always visible, details behind a toggle */}
        {summary && (
          <Collapsible
            label="Role overview"
            testId="demo-role-overview"
            summary={summary.headline}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-border border border-border">
              <div className="bg-background p-4">
                <div className="terminal-label mb-2">What you'd do</div>
                <ul className="space-y-1 text-sm text-foreground/85">
                  {(summary.responsibilities || []).map((r, i) => <li key={i}>· {r}</li>)}
                </ul>
              </div>
              <div className="bg-background p-4">
                <div className="terminal-label text-accent-amber mb-2">Must-haves</div>
                <ul className="space-y-1 text-sm text-foreground/85">
                  {(summary.must_haves || []).map((r, i) => <li key={i}>· {r}</li>)}
                </ul>
              </div>
              <div className="bg-background p-4">
                <div className="terminal-label text-accent-green mb-2">Nice-to-haves</div>
                <ul className="space-y-1 text-sm text-foreground/85">
                  {(summary.nice_to_haves || []).map((r, i) => <li key={i}>· {r}</li>)}
                </ul>
              </div>
              <div className="bg-background p-4">
                <div className="terminal-label mb-2">Who they want</div>
                <p className="text-sm text-foreground/85">{summary.ideal_candidate}</p>
              </div>
            </div>
          </Collapsible>
        )}
        {/* spacer so the next section keeps the panel border style */}
        <div className="mt-px" />

        {phase === "ready" && (
          <>
          <div className="grid grid-cols-1 gap-px bg-border border border-border" data-testid="demo-materials">
            <Section title="Tailored resume" testId="materials-resume" tailored>
              <pre className="text-xs font-mono whitespace-pre-wrap leading-relaxed text-foreground/90 max-h-[600px] overflow-y-auto">{mats.resume}</pre>
            </Section>
            <Collapsible
              label="Cover letter"
              testId="materials-cover-letter"
              accent="amber"
              summary={`${(mats.cover_letter || "").split(/\s+/).filter(Boolean).length} words. Tailored to ${job.company} in ${persona.name.split(" ")[0]}'s voice. Click to read.`}
              rightSlot={<span className="hidden sm:inline-flex items-center gap-1 text-[9px] tracking-[0.22em] uppercase font-mono px-2 py-0.5 border border-accent-amber/60 text-accent-amber bg-accent-amber/5"><Sparkles className="w-2.5 h-2.5" />Tailored</span>}
            >
              <pre className="text-xs font-mono whitespace-pre-wrap leading-relaxed text-foreground/90 max-h-[600px] overflow-y-auto">{mats.cover_letter}</pre>
            </Collapsible>
            <div className="bg-background p-6 relative" style={{ boxShadow: "inset 4px 0 0 0 hsl(var(--accent-amber) / 0.7)" }}>
              <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                <div className="terminal-label text-primary">[ PORTFOLIO.PAGE ]</div>
                <TailoredPill label="Pieces + descriptions chosen by AI for this job" />
              </div>
              <p className="text-sm text-foreground/85 mb-5 italic border-l-2 border-accent-amber/70 pl-3">{mats.portfolio_intro}</p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {selected_pieces.map((rawP) => {
                  const p = normalizePiece(rawP);
                  const Icon = PIECE_ICONS[p.piece_type] || ImageIcon;
                  const thumb = rawP.image_url || rawP.image_urls?.[0]
                    || rawP.case_study?.problem?.image_urls?.[0]
                    || rawP.case_study?.process?.image_urls?.[0]
                    || rawP.case_study?.outcome?.image_urls?.[0];
                  const originalDesc = originalById[p.id]?.description || "";
                  const descRewritten = !!(mats.portfolio_piece_descriptions || {})[p.id];
                  const csRewritten = !!(mats.portfolio_case_study_rewrites || {})[p.id];
                  const anyRewrite = descRewritten || csRewritten;
                  return (
                    <div
                      key={p.id}
                      className="panel p-3 relative"
                      data-testid={`demo-piece-${p.id}`}
                      style={anyRewrite ? { borderColor: "hsl(var(--accent-amber) / 0.6)" } : undefined}
                    >
                      {anyRewrite && (
                        <span className="absolute -top-2 left-2 text-[8px] tracking-[0.25em] uppercase font-mono px-1.5 py-0.5 bg-accent-amber text-background z-10">
                          Tailored
                        </span>
                      )}
                      <div className="aspect-video bg-surface mb-2 flex items-center justify-center overflow-hidden">
                        {thumb ? <img src={thumb} alt={p.title} className="w-full h-full object-cover" /> : <Icon className="w-6 h-6 text-muted-foreground" strokeWidth={1.25} />}
                      </div>
                      <div className="text-xs font-mono font-medium truncate">{p.title}</div>
                      <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground truncate">{p.work_type}</div>
                      {p.description && (
                        <div className="mt-2 group/desc relative">
                          <p className={`text-[11px] text-foreground/75 line-clamp-3 ${descRewritten ? "bg-accent-amber/10 px-1.5 py-1 -mx-1 border-l-2 border-accent-amber/60" : ""}`}>{p.description}</p>
                          {descRewritten && originalDesc && (
                            <div className="hidden group-hover/desc:block absolute z-20 left-0 right-0 top-full mt-1 panel p-2 shadow-lg" style={{ borderColor: "hsl(var(--accent-amber) / 0.4)" }}>
                              <div className="text-[8px] tracking-[0.25em] uppercase font-mono text-accent-amber mb-1">Original (untailored)</div>
                              <p className="text-[10px] text-foreground/70 leading-snug">{originalDesc}</p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              <p className="text-[10px] mt-4 text-muted-foreground italic">Hover any tailored description to see the original profile copy.</p>
            </div>
          </div>

          {/* Send → Run ATS Simulation CTA */}
          <div className="panel p-6 mt-px flex items-center justify-between flex-wrap gap-3">
            <div className="text-xs text-muted-foreground max-w-xl leading-relaxed">
              In the real product this is your &quot;Send application&quot; button. For the demo, we replace it with an ATS A/B comparison so you can see exactly how much the tailoring lifts the resume score.
            </div>
            <button
              data-testid="demo-run-ats-btn"
              onClick={() => navigate(`/demo/applications/${jobId}/ats?persona=${personaId}`)}
              className="btn-primary inline-flex items-center gap-2"
            >
              <Beaker className="w-3 h-3" /> Run ATS simulation <ArrowRight className="w-3 h-3" />
            </button>
          </div>
          </>
        )}
      </div>

      {/* Analyzing overlay (full-page) */}
      {phase === "analyzing" && (
        <AnalyzingOverlay persona={persona} job={job} stepIdx={stepIdx} />
      )}
    </DemoLayout>
  );
}

function Score({ label, value, color }) {
  return (
    <div className="text-right leading-none">
      <div className="text-[9px] tracking-[0.25em] uppercase font-mono mb-0.5" style={{ color }}>{label}</div>
      <div className="font-display text-3xl tabular-nums" style={{ color }}>{value}</div>
    </div>
  );
}

function Section({ title, testId, tailored, children }) {
  return (
    <div className="bg-background p-6 relative" data-testid={testId} style={tailored ? { boxShadow: "inset 4px 0 0 0 hsl(var(--accent-amber) / 0.7)" } : undefined}>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="terminal-label text-primary">[ {title.toUpperCase()} ]</div>
        {tailored && <TailoredPill label="Rewritten by AI for this posting" />}
      </div>
      {children}
    </div>
  );
}

function TailoredPill({ label }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-[9px] tracking-[0.22em] uppercase font-mono px-2 py-0.5 border border-accent-amber/60 text-accent-amber bg-accent-amber/5">
      <Sparkles className="w-2.5 h-2.5" strokeWidth={2} />
      {label}
    </span>
  );
}

function AnalyzingOverlay({ persona, job, stepIdx }) {
  const visible = ANALYZE_STEPS.slice(0, Math.min(stepIdx + 1, ANALYZE_STEPS.length))
    .map((s) => s.replaceAll("[PERSONA]", persona.name.split(" ")[0]));
  return (
    <div className="fixed inset-0 z-40 bg-background/95 backdrop-blur-sm flex items-center justify-center p-6" data-testid="demo-analyzing-overlay">
      <div className="max-w-md w-full">
        <div className="terminal-label text-primary mb-4 text-center">[ TAILORING.MATERIALS ]</div>
        <h2 className="font-display text-2xl uppercase font-bold tracking-tight text-center mb-2">{job.title}</h2>
        <p className="text-xs text-muted-foreground text-center mb-8">{job.company} · {job.location}</p>
        <div className="font-mono text-xs space-y-2 text-foreground/80">
          {visible.map((line, i) => (
            <div key={i} className="flex items-start gap-2">
              <span className="text-primary mt-0.5">{i === visible.length - 1 ? <Loader2 className="w-3 h-3 animate-spin inline" /> : "✓"}</span>
              <span>{line}</span>
            </div>
          ))}
        </div>
        <p className="mt-6 text-[10px] uppercase tracking-[0.22em] text-center text-muted-foreground/70">
          Pre-baked for the demo. Sign in to run this on a real posting.
        </p>
      </div>
    </div>
  );
}
